import React from 'react';
import { get, find, forEach, cloneDeep, head, size } from 'lodash';
import searchQuery from 'query-string';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import shortid from 'shortid';

// local dependencies
import { REDUCER, MEMBER_SEARCH_MODULE } from 'constants/entity-names';
import { memberSearchWidget as MemberSearchWidgetView } from 'ui/views/widgets/memberSearchWidget';
import { SERVICE_RESPONSE_STATUS, SIZES } from 'constants/common';
import { MemberSearchWidgetService } from 'services/memberSearchWidget';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { updateCallState, initCallState } from 'store/actions/callState';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import { getLabel } from 'utils/getLabel';

// import { overlay } from 'modules/overlay';
// import { OVERLAY_VIEWS } from 'constants/templates';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberSearchWidget } from 'ui/containers/MemberSearchWidget';
 *
 * // render
 * <MemberSearchWidget propName='propValue'/>
 */
class _MemberSearchWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods
        this.handleActionClick = this.handleActionClick.bind( this );
        this.startOutboundCall = this.startOutboundCall.bind( this );
        this.liveSelectionActive = true;
        this.liveSelectionPending = false;

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            isSelectingLive: false
        };

        // create service instance
        this.memberSearchWidgetService = new MemberSearchWidgetService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'MemberSearchWidget.fetchComponentData()' );

        // cancel ongoing request
        this.memberSearchWidgetService.cancel();

        // fetch component data
        const { status, data,isRoleAvailable } = await this.memberSearchWidgetService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        if(!isRoleAvailable){
            errorOverlay.open( { message: "User Role cannot be found. Please contact your administrator for details", size: SIZES.SMALL } );
        }else{
 
        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }
}

    /**
     * @desc Shows an overlay with phone numbers to MSR
     */
    async initiateOutboundCall( { url, payload } ) {
        log.debug( 'MemberSearchWidget.initiateOutboundCall()' );

        // cancel ongoing request
        this.memberSearchWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status, data } = await this.memberSearchWidgetService.initiateOutboundCall( {
            url: url,
            data: {
                data: payload
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                const _data = cloneDeep( data );

                // iterate over phone number options
                forEach( get( _data, 'data.options.phoneNumberOptions', [] ), ( option ) => {

                    // generate unique id for each option
                    option.actualValue = option.value;
                    option.value = shortid.generate();
                    
                    // set suffix and default value based on option type
                    let suffix;
                    if ( option.isDefault ) {
                        suffix = getLabel( get( head( get( _data, 'fields', [] ) ), 'config.isDefaultTitle', {} ) );
                        _data.values = { 'phoneNumber': option.value };
                    } else {
                        suffix = getLabel( get( find( get( _data, 'data.options.contactType', [] ), { value: option.type }, {} ), 'title', {} ) );
                    }

                    // modify option title with suffix
                    option.title.value = `${option.title.value} (${suffix})`;
                } );

                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.SET_PHONE_NUMBER,
                        showCloseButton: false,
                        ignoreOutsideClick: false,
                    },
                    config: {},
                    data: {
                        ..._data,
                        payload,
                        onFormSubmit: this.startOutboundCall,
                        onCancelClick: () => {
                            overlay.close();
                        },
                    }
                } );
            } );
            
        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * @desc Request backend to start an outbound call for member.
     */
    async startOutboundCall( { actions, payload, formData, options } ) {
        log.debug( 'MemberSearchWidget.startOutboundCall()' );

        // cancel ongoing request
        this.memberSearchWidgetService.cancel();

        // close overlay
        overlay.close();

        // show page level preloader
        pageLevelPreloader.show();

        // find Start Call action
        const startCallAction = find( actions, { name: 'submit' }, {} );

        // get actual value of phone number based on its shortid (coming as formData.phoneNumber)
        const phoneNumber = get( find( options, { value: formData.phoneNumber } ), 'actualValue' );

        // fetch data
        const { status, data } = await this.memberSearchWidgetService.startOutboundCall( {
            url: startCallAction.url,
            data: {
                data: {
                    ...payload,
                    phoneNumber
                }
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {
                this.props.initCallState( data.callData ); // save call data in the store
                
                // change to authorization flow route, 2nd step
                this.props.history.push( {
                    pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                    search: searchQuery.stringify( {
                        step: 2,
                    } ),
                } );
                
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }
    
    /**
     * @desc Action handler for column action and group action.
     */
    async handleActionClick( { type = 'column', value = {}, item = {}, liveFallback = false, liveMemberLookup = false } = {} ) {
        log.debug( 'MemberSearchWidget.handleActionClick()' ); // @TODO: remove
        if( this.liveSelectionPending ) return;
        const firstName = get( item, 'values.firstName', '' );
        const lastName = get( item, 'values.lastName', '' );

        // when `memberId` is clicked, redirect to member detail page
        if( 'column' === type && 'memberId' === value.name ) {
            const collisionEnabled = true === get( this.state, 'componentData.data.state.indexedCollisionSelectionEnabled' );
            if( true === item.indexedSelectionConflict && false === liveFallback && false === liveMemberLookup && collisionEnabled ) {
                const url = get( this.state, 'componentData.elements.urls.selectIndexedCollision' );
                const patientId = get( item, 'values.patientId' );
                if( ! url || 'string' !== typeof patientId || ! patientId.trim() ) {
                    errorOverlay.open();
                    return;
                }
                this.liveSelectionPending = true;
                this.setState( { isSelectingLive: true } );
                pageLevelPreloader.show();
                try {
                    const result = await this.memberSearchWidgetService.selectIndexedCollision( {
                        url,
                        data: { context: { memberId: item.id, patientId } },
                    } );
                    if( ! this.liveSelectionActive ) return;
                    const verified = get( result, 'data.state' );
                    if( SERVICE_RESPONSE_STATUS.SUCCESS !== get( result, 'status' ) || ! verified ||
                        verified.memberId !== item.id || verified.patientId !== patientId || false !== verified.liveFallback ) {
                        errorOverlay.open();
                        return;
                    }
                    this.props.history.push( {
                        pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                        state: { memberId: verified.memberId, patientId: verified.patientId, liveFallback: false }
                    } );
                } catch( error ) {
                    if( this.liveSelectionActive ) errorOverlay.open();
                } finally {
                    if( this.liveSelectionActive ) {
                        pageLevelPreloader.hide();
                        this.setState( { isSelectingLive: false } );
                    }
                    this.liveSelectionPending = false;
                }
                return;
            }
            const enabled = true === get( this.state, 'componentData.data.state.liveSelectionEnabled' );
            if( true === liveMemberLookup && enabled ) {
                const url = get( this.state, 'componentData.elements.urls.selectLiveMember' );
                if( ! url ) {
                    errorOverlay.open();
                    return;
                }
                this.liveSelectionPending = true;
                this.setState( { isSelectingLive: true } );
                pageLevelPreloader.show();
                try {
                    const result = await this.memberSearchWidgetService.selectLiveMember( {
                        url,
                        data: { context: { memberId: item.id, patientId: get( item, 'values.patientId', null ) } },
                    } );
                    if( ! this.liveSelectionActive ) return;
                    const verified = get( result, 'data.state' );
                    if( SERVICE_RESPONSE_STATUS.SUCCESS !== get( result, 'status' ) ||
                        ! verified || verified.memberId !== item.id || true !== verified.liveFallback ||
                        ! Object.prototype.hasOwnProperty.call( verified, 'patientId' ) ||
                        'string' !== typeof verified.patientId || ! verified.patientId.trim() ) {
                        errorOverlay.open();
                        return;
                    }
                    this.props.history.push( {
                        pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                        state: { memberId: verified.memberId, patientId: verified.patientId, liveFallback: true }
                    } );
                } catch( error ) {
                    if( this.liveSelectionActive ) errorOverlay.open();
                } finally {
                    if( this.liveSelectionActive ) {
                        pageLevelPreloader.hide();
                        this.setState( { isSelectingLive: false } );
                    }
                    this.liveSelectionPending = false;
                }
                return;
            }
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                state: { memberId: item.id, patientId: item.values.patientId, liveFallback: true === liveFallback }
            } );
        }

        /*---------*/

         // when `initiateCall` group action is clicked, initiate outbound call
        if( 'groupAction' === type && 'initiateCall' === value.name ) {
            return this.initiateOutboundCall( {
                actionName: value.name,
                url: value.url,
                payload: {
                    memberId: item.id,
                    firstName,
                    lastName,
                    patientId: item.values.patientId
                },
            } );
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.liveSelectionActive = false;
        if( this.liveSelectionPending ) pageLevelPreloader.hide();
        this.memberSearchWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <>
            <MemberSearchWidgetView
                isCallInitiated = { this.state.isCallInitiated }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                onActionClick={ this.handleActionClick }
                requestPayload= { this.props.requestPayload }
            />
            <span role="status" aria-live="polite">{ this.state.isSelectingLive ? 'Checking member access…' : '' }</span>
            </>
        );
    }
}

// set display name
_MemberSearchWidget.displayName = '_MemberSearchWidget';

// set default props
_MemberSearchWidget.defaultProps = {
    widget: {}, // widget configuration
    requestPayload: {
        context: {
            module: MEMBER_SEARCH_MODULE.DASHBOARD
        }
   }
};

// prop types
_MemberSearchWidget.propTypes = {};

/*********************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};


// wrap container with router
export const MemberSearchWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _MemberSearchWidget ) );
